import { createFileRoute, Link } from "@tanstack/react-router";

const TITLE = "Perimenopause Heart Palpitations: What to Know";
const DESCRIPTION =
  "Why heart palpitations spike during perimenopause, how estrogen drives them, what patterns to track, and when racing heartbeats need a doctor.";
const URL = "https://knowmeno.com/guide/heart-palpitations";

export const Route = createFileRoute("/guide/heart-palpitations")({
  head: () => ({
    meta: [
      { title: `${TITLE} — KnowMeno` },
      { name: "description", content: DESCRIPTION },
      { name: "robots", content: "index, follow, max-image-preview:large" },
      { property: "og:title", content: TITLE },
      { property: "og:description", content: DESCRIPTION },
      { property: "og:type", content: "article" },
      { property: "og:url", content: URL },
      { name: "twitter:title", content: TITLE },
      { name: "twitter:description", content: DESCRIPTION },
    ],
    links: [
      { rel: "canonical", href: URL },
      { rel: "alternate", hrefLang: "en", href: URL },
      { rel: "alternate", hrefLang: "x-default", href: URL },
    ],
    scripts: [
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "Article",
          headline: TITLE,
          image: ["https://knowmeno.com/og-image.png?v=2"],
          datePublished: "2026-01-15",
          dateModified: "2026-06-29",
          inLanguage: "en",
          description: DESCRIPTION,
          author: { "@type": "Organization", name: "KnowMeno" },
          publisher: {
            "@type": "Organization",
            name: "KnowMeno",
            logo: { "@type": "ImageObject", url: "https://knowmeno.com/icon-512.png" },
          },
          mainEntityOfPage: URL,
        }),
      },
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "FAQPage",
          mainEntity: [
            {
              "@type": "Question",
              name: "Are heart palpitations a normal symptom of perimenopause?",
              acceptedAnswer: {
                "@type": "Answer",
                text: "Yes. Up to roughly 40% of women report new or worsening palpitations during perimenopause. They are usually driven by fluctuating estrogen affecting the autonomic nervous system, and most are benign — but new palpitations should still be evaluated once to rule out arrhythmia, thyroid disease, or anemia.",
              },
            },
            {
              "@type": "Question",
              name: "Why does estrogen cause heart palpitations?",
              acceptedAnswer: {
                "@type": "Answer",
                text: "Estrogen modulates the autonomic nervous system and influences the heart's electrical conduction. As estrogen swings up and down in perimenopause, sympathetic (fight-or-flight) activity can spike, raising heart rate, causing skipped or extra beats, and producing the fluttering sensation many women describe.",
              },
            },
            {
              "@type": "Question",
              name: "When should I see a doctor about menopause palpitations?",
              acceptedAnswer: {
                "@type": "Answer",
                text: "Seek same-day care if palpitations come with chest pain, fainting, severe shortness of breath, or last more than a few minutes at a very fast rate. Book a routine appointment if they are new, frequent, waking you at night, or accompanied by weight changes, tremor, or unusual fatigue — those can suggest thyroid or anemia rather than hormones.",
              },
            },
            {
              "@type": "Question",
              name: "Does HRT help with perimenopause palpitations?",
              acceptedAnswer: {
                "@type": "Answer",
                text: "For many women, stabilizing estrogen with menopausal hormone therapy reduces palpitations along with hot flashes and night sweats. Response varies, so tracking frequency before and after starting HRT helps you and your clinician judge whether it is working.",
              },
            },
          ],
        }),
      },
    ],
  }),
  component: HeartPalpitationsGuide,
});

const PLUM = "#4B1F4F";
const PINK = "#FF5C7A";
const CREAM = "#FAF7F5";
const BORDER = "#E8D5DA";
const TEXT = "#2D1A2E";
const MUTED = "#6E4B71";

interface TriggerRow {
  trigger: string;
  why: string;
  bg: string;
}

const TRIGGERS: TriggerRow[] = [
  {
    trigger: "Hot flashes & night sweats",
    why: "The same vasomotor surge that triggers a flash also spikes heart rate. Palpitations often start seconds before the flash.",
    bg: "#FFFFFF",
  },
  {
    trigger: "Caffeine",
    why: "A more reactive autonomic system in perimenopause amplifies caffeine's stimulant effect. A coffee you tolerated for years can suddenly cause flutters.",
    bg: "#FFF4EC",
  },
  {
    trigger: "Alcohol",
    why: "Alcohol disrupts sleep and dehydrates — both lower the threshold for ectopic beats. Red wine is a frequent trigger.",
    bg: "#FFFFFF",
  },
  {
    trigger: "Stress & poor sleep",
    why: "Cortisol and adrenaline are already running higher during perimenopause. Add a bad night's sleep and the heart feels it.",
    bg: "#FFE7DB",
  },
  {
    trigger: "Lying on your left side",
    why: "Brings the heart closer to the chest wall, making normal beats feel pronounced. Common at night.",
    bg: "#FFFFFF",
  },
  {
    trigger: "Low iron or low ferritin",
    why: "Heavy perimenopausal periods can drop iron stores, forcing the heart to work harder and producing a pounding sensation.",
    bg: "#FFE4EA",
  },
];

function HeartPalpitationsGuide() {
  return (
    <div
      style={{
        background: CREAM,
        minHeight: "100vh",
        fontFamily: "system-ui, -apple-system, sans-serif",
        color: TEXT,
        padding: "32px 16px 80px",
      }}
    >
      <article
        style={{
          maxWidth: 720,
          margin: "0 auto",
          background: "#FFFFFF",
          borderRadius: 20,
          padding: "32px 24px",
          border: `0.5px solid ${BORDER}`,
        }}
      >
        <nav style={{ fontSize: 12, color: MUTED, marginBottom: 16 }}>
          <Link to="/" style={{ color: MUTED, textDecoration: "none" }}>
            KnowMeno
          </Link>{" "}
          / Guides / Heart Palpitations
        </nav>

        <h1
          style={{
            fontSize: 32,
            lineHeight: 1.15,
            color: PLUM,
            margin: "0 0 12px",
            fontWeight: 500,
            letterSpacing: "-0.02em",
          }}
        >
          Perimenopause Heart Palpitations
        </h1>
        <p style={{ fontSize: 16, lineHeight: 1.6, color: MUTED, marginTop: 0 }}>
          Why a fluttering, racing, or skipping heartbeat is one of the most under-discussed
          perimenopause symptoms — and how to tell a hormone-driven episode from one that needs a
          doctor's attention.
        </p>

        <section style={{ marginTop: 28 }}>
          <h2 style={{ fontSize: 22, color: PLUM, fontWeight: 500, marginBottom: 8 }}>
            What palpitations actually feel like
          </h2>
          <p style={{ fontSize: 15, lineHeight: 1.7 }}>
            Women describe perimenopausal palpitations in a few recognizable ways: a
            <strong> sudden thump </strong>in the chest, a <strong>flutter or flip</strong>, a
            short burst of <strong>racing</strong> heartbeats, or the unsettling feeling that the
            heart <strong>skipped a beat</strong>. Episodes are usually brief — seconds to a
            minute — and often cluster around hot flashes, falling asleep, or waking in the early
            hours.
          </p>
        </section>

        <section style={{ marginTop: 28 }}>
          <h2 style={{ fontSize: 22, color: PLUM, fontWeight: 500, marginBottom: 8 }}>
            Why estrogen fluctuations cause them
          </h2>
          <p style={{ fontSize: 15, lineHeight: 1.7 }}>
            Estrogen receptors sit throughout the autonomic nervous system and the heart's
            electrical pathways. When estrogen swings — the hallmark of perimenopause — the balance
            between sympathetic (fight-or-flight) and parasympathetic (rest) tone tips. The result
            is a more reactive heart: faster baseline rate, more ectopic beats, and a stronger
            response to caffeine, stress, and poor sleep than you had in your 30s.
          </p>
          <p style={{ fontSize: 15, lineHeight: 1.7 }}>
            That's also why palpitations frequently <strong>travel with hot flashes</strong>. Both
            are driven by the same vasomotor instability — the heart-rate spike often arrives a
            beat or two before the heat does.
          </p>
        </section>

        <section style={{ marginTop: 28 }}>
          <h2 style={{ fontSize: 22, color: PLUM, fontWeight: 500, marginBottom: 12 }}>
            Common triggers to watch for
          </h2>
          <div
            style={{
              border: `1px solid ${BORDER}`,
              borderRadius: 12,
              overflow: "hidden",
            }}
          >
            <table
              style={{
                width: "100%",
                borderCollapse: "collapse",
                fontSize: 14,
              }}
            >
              <thead>
                <tr style={{ background: PLUM, color: "#FFFFFF", textAlign: "left" }}>
                  <th style={{ padding: "10px 12px", fontWeight: 500 }}>Trigger</th>
                  <th style={{ padding: "10px 12px", fontWeight: 500 }}>Why it matters in perimenopause</th>
                </tr>
              </thead>
              <tbody>
                {TRIGGERS.map((r) => (
                  <tr key={r.trigger} style={{ background: r.bg, borderTop: `1px solid ${BORDER}` }}>
                    <td style={{ padding: "12px", fontWeight: 500, verticalAlign: "top", whiteSpace: "nowrap" }}>
                      {r.trigger}
                    </td>
                    <td style={{ padding: "12px", color: MUTED, lineHeight: 1.55 }}>{r.why}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <p style={{ fontSize: 12, color: MUTED, marginTop: 8, lineHeight: 1.55 }}>
            Most women have two or three personal triggers. Identifying yours is the single most
            useful thing you can do.
          </p>
        </section>

        <section style={{ marginTop: 28 }}>
          <h2 style={{ fontSize: 22, color: PLUM, fontWeight: 500, marginBottom: 8 }}>
            When palpitations warrant a doctor visit
          </h2>
          <p style={{ fontSize: 15, lineHeight: 1.7 }}>
            Most perimenopausal palpitations are benign, but you should always have new palpitations
            evaluated <strong>at least once</strong> to rule out arrhythmia (especially atrial
            fibrillation), thyroid disease, and anemia. Get same-day care if palpitations come with
            any of the following:
          </p>
          <ul style={{ fontSize: 15, lineHeight: 1.8, paddingLeft: 20 }}>
            <li>Chest pain or pressure</li>
            <li>Fainting or near-fainting</li>
            <li>Severe shortness of breath</li>
            <li>A racing heartbeat that lasts more than a few minutes and won't slow</li>
            <li>A new irregular pulse you can feel at your wrist</li>
          </ul>
          <p style={{ fontSize: 15, lineHeight: 1.7 }}>
            Book a routine appointment — not an emergency — if episodes are simply more frequent,
            waking you at night, or accompanied by weight changes, tremor, hair loss, or unusual
            fatigue. Those clues often point to <strong>thyroid</strong> rather than hormones.
          </p>
        </section>

        <section style={{ marginTop: 28 }}>
          <h2 style={{ fontSize: 22, color: PLUM, fontWeight: 500, marginBottom: 8 }}>
            What helps
          </h2>
          <ul style={{ fontSize: 15, lineHeight: 1.8, paddingLeft: 20 }}>
            <li>
              <strong>Track the pattern.</strong> Time of day, what you ate or drank, sleep the
              night before, whether a hot flash was involved. A two-week log usually exposes the
              personal triggers.
            </li>
            <li>
              <strong>Cut or downshift caffeine and alcohol</strong> for two weeks as a test, then
              reintroduce one at a time.
            </li>
            <li>
              <strong>Protect sleep.</strong> Cool the bedroom, limit alcohol within three hours of
              bed, and treat night sweats — better sleep means fewer night-time palpitations.
            </li>
            <li>
              <strong>Check iron and thyroid</strong> with your clinician, particularly if you've
              had heavy perimenopausal periods.
            </li>
            <li>
              <strong>Consider HRT / MHT.</strong> Stabilizing estrogen often calms both hot
              flashes and the palpitations that travel with them.
            </li>
            <li>
              <strong>Slow-breathing or vagal techniques</strong> (4-7-8 breathing, cold water on
              the face) can shorten an episode in real time.
            </li>
          </ul>
        </section>

        <section style={{ marginTop: 28 }}>
          <h2 style={{ fontSize: 22, color: PLUM, fontWeight: 500, marginBottom: 8 }}>
            How to use KnowMeno to make the visit count
          </h2>
          <p style={{ fontSize: 15, lineHeight: 1.7 }}>
            Doctors get more from "I had 14 palpitation episodes in the last three weeks, most
            around 3 a.m., usually preceded by a hot flash" than from "they happen sometimes."
            KnowMeno tracks symptom frequency, timing, and the other patterns clustered around
            them — so you walk into the appointment with a clear chart instead of trying to
            remember the last month from memory.
          </p>
          <div style={{ marginTop: 18, display: "flex", gap: 12, flexWrap: "wrap" }}>
            <Link
              to="/sign-up"
              style={{
                background: PLUM,
                color: "#FFFFFF",
                padding: "12px 18px",
                borderRadius: 12,
                textDecoration: "none",
                fontSize: 14,
                fontWeight: 500,
              }}
            >
              Start tracking free for 7 days
            </Link>
            <Link
              to="/"
              style={{
                background: "transparent",
                color: PLUM,
                padding: "12px 18px",
                borderRadius: 12,
                textDecoration: "none",
                fontSize: 14,
                fontWeight: 500,
                border: `1px solid ${PLUM}`,
              }}
            >
              How KnowMeno works
            </Link>
          </div>
        </section>

        <section style={{ marginTop: 32 }}>
          <h2 style={{ fontSize: 22, color: PLUM, fontWeight: 500, marginBottom: 12 }}>
            Frequently asked questions
          </h2>

          <details style={{ borderTop: `1px solid ${BORDER}`, padding: "12px 0" }}>
            <summary style={{ cursor: "pointer", fontWeight: 500, fontSize: 15, color: PLUM }}>
              Are heart palpitations a normal symptom of perimenopause?
            </summary>
            <p style={{ fontSize: 14, lineHeight: 1.65, color: MUTED, marginTop: 8 }}>
              Yes. They're reported by up to about 40% of women in the transition, driven by
              estrogen swings. New palpitations should still be checked once to rule out other
              causes.
            </p>
          </details>

          <details style={{ borderTop: `1px solid ${BORDER}`, padding: "12px 0" }}>
            <summary style={{ cursor: "pointer", fontWeight: 500, fontSize: 15, color: PLUM }}>
              Why does estrogen cause heart palpitations?
            </summary>
            <p style={{ fontSize: 14, lineHeight: 1.65, color: MUTED, marginTop: 8 }}>
              Estrogen modulates the autonomic nervous system and the heart's electrical activity.
              When estrogen fluctuates, sympathetic tone rises and the heart beats faster, stronger,
              or with more ectopic beats.
            </p>
          </details>

          <details style={{ borderTop: `1px solid ${BORDER}`, padding: "12px 0" }}>
            <summary style={{ cursor: "pointer", fontWeight: 500, fontSize: 15, color: PLUM }}>
              When should I see a doctor about menopause palpitations?
            </summary>
            <p style={{ fontSize: 14, lineHeight: 1.65, color: MUTED, marginTop: 8 }}>
              Same-day care for chest pain, fainting, severe breathlessness, or sustained racing.
              Routine appointment for new, frequent, or sleep-disturbing palpitations, or anything
              that comes with tremor, weight change, or unusual fatigue.
            </p>
          </details>

          <details
            style={{
              borderTop: `1px solid ${BORDER}`,
              borderBottom: `1px solid ${BORDER}`,
              padding: "12px 0",
            }}
          >
            <summary style={{ cursor: "pointer", fontWeight: 500, fontSize: 15, color: PLUM }}>
              Does HRT help with perimenopause palpitations?
            </summary>
            <p style={{ fontSize: 14, lineHeight: 1.65, color: MUTED, marginTop: 8 }}>
              For many women, yes — stabilizing estrogen reduces palpitations along with vasomotor
              symptoms. Track frequency before and after starting HRT so you can tell whether it's
              working for you specifically.
            </p>
          </details>
        </section>

        <p
          style={{
            marginTop: 28,
            fontSize: 12,
            color: MUTED,
            lineHeight: 1.6,
            fontStyle: "italic",
            borderTop: `1px solid ${BORDER}`,
            paddingTop: 16,
          }}
        >
          This guide is for general education and is not medical advice. New, severe, or persistent
          palpitations should always be evaluated by a qualified clinician.
        </p>

        <div style={{ marginTop: 24, textAlign: "center" }}>
          <Link
            to="/"
            style={{ color: PINK, fontSize: 14, textDecoration: "none", fontWeight: 500 }}
          >
            ← Back to KnowMeno
          </Link>
        </div>
      </article>
    </div>
  );
}
